import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Skeleton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

/*
  Genel Durum İzleme ekranının ortak parçaları (apexcharts yerine hafif HTML/CSS grafikler).
  Renk: tek seri, marka turuncusunun koyu tonu (#EF6C00, beyaz zeminde 3:1 kontrast).
  Değer ve etiketler grafik renginde değil, metin tonlarında yazılır.
*/
export const BAR = '#EF6C00';
export const BAR_HOVER = '#E65100';
export const TRACK = '#FFE0B2';
// İkinci seri (halka / donut): turuncu ile doğrulandı (CVD ΔE 23.7, kontrast >= 3:1)
export const SECOND = '#3F6FB5';
export const SECOND_TRACK = '#DCE6F5';
const INK = '#263238';
const INK_2 = '#546e7a';
const MUTED = '#90a4ae';
const GRID = '#eceff1';
const DARK = '#424242';
const ORANGE = '#FF9800';

const nf = new Intl.NumberFormat('tr-TR');
export const formatNumber = (v) => nf.format(Number(v) || 0);
export const formatCurrency = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(Number(v) || 0);
export const formatPercent = (part, total) => (total > 0 ? `%${Math.round((part / total) * 100)}` : '%0');

// ---------------------------------------------------------------- Açılış animasyonu
// İlk çizimde (ve veri değişince) 0'dan hedef değere büyüsün diye bir kare sonra true döner.
// key: verinin içeriği (string) — her çizimde yeni dizi üretilse de animasyon sadece veri değişince oynar.
export function useGrowIn(key) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(false);
    let id2;
    const id1 = window.requestAnimationFrame(() => {
      id2 = window.requestAnimationFrame(() => setReady(true));
    });
    return () => {
      window.cancelAnimationFrame(id1);
      window.cancelAnimationFrame(id2);
    };
  }, [key]);
  return ready;
}

const dataKey = (items) => JSON.stringify((items || []).map((x) => [x.label, Number(x.value) || 0, Number(x.total) || 0]));

// Sayı 0'dan hedefe doğru sayarak gelir
export function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const end = Number(target) || 0;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(end * eased);
      if (t < 1) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

// ---------------------------------------------------------------- Kart
export function MonitorCard({ title, subtitle, action, children, minHeight }) {
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '16px',
        border: '1px solid #e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          px: 2,
          py: 1.25,
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: DARK, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            {title}
          </Typography>
          {subtitle && <Typography sx={{ fontSize: '11px', color: '#757575' }}>{subtitle}</Typography>}
        </Box>
        {action}
      </Box>
      <Box sx={{ p: 2, flex: 1, minHeight }}>{children}</Box>
    </Card>
  );
}

// ---------------------------------------------------------------- Yükleniyor / boş
export function ChartSkeleton({ height = 260 }) {
  return <Skeleton variant="rectangular" height={height} sx={{ borderRadius: '12px' }} />;
}

export function EmptyState({ text = 'Gösterilecek veri yok.', height = 200 }) {
  return (
    <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '12px' }}>
      {text}
    </Box>
  );
}

const tooltipContent = (label, value, share, valueFormatter) => (
  <Box sx={{ fontSize: '12px', lineHeight: 1.6 }}>
    <div style={{ fontWeight: 700 }}>{label}</div>
    <div>
      {valueFormatter(value)}
      {share ? ` · ${share}` : ''}
    </div>
  </Box>
);

// ---------------------------------------------------------------- Yatay çubuk listesi (kategoriler)
// items: [{ label, value }] — büyükten küçüğe sıralanır, değer çubuğun ucunda yazar
export function BarList({ items, valueFormatter = formatNumber, maxHeight }) {
  const grown = useGrowIn(dataKey(items));
  const rows = [...(items || [])].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
  const max = Math.max(1, ...rows.map((r) => Number(r.value) || 0));
  const total = rows.reduce((s, r) => s + (Number(r.value) || 0), 0);

  if (rows.length === 0) return <EmptyState />;

  return (
    <Box sx={{ maxHeight, overflowY: maxHeight ? 'auto' : 'visible', pr: maxHeight ? 1 : 0 }}>
      {rows.map((r) => {
        const v = Number(r.value) || 0;
        const share = formatPercent(v, total);
        return (
          <Tooltip key={r.label} title={tooltipContent(r.label, v, share, valueFormatter)} placement="top" arrow followCursor>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(110px, 32%) 1fr',
                alignItems: 'center',
                gap: 1.5,
                py: '7px',
                px: 0.5,
                borderRadius: '8px',
                cursor: 'default',
                '&:hover': { backgroundColor: '#fff8e1' },
                '&:hover .bar': { backgroundColor: BAR_HOVER }
              }}
            >
              <Typography sx={{ fontSize: '12px', color: INK_2, fontWeight: 600 }} noWrap title={r.label}>
                {r.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box
                  className="bar"
                  sx={{
                    height: 16,
                    width: grown ? `${(v / max) * 100}%` : '0%',
                    minWidth: v > 0 ? 4 : 0,
                    backgroundColor: BAR,
                    borderRadius: '0 4px 4px 0',
                    transition: 'width 0.7s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.15s',
                    flexShrink: 1
                  }}
                />
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: INK, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {valueFormatter(v)}
                  <Box component="span" sx={{ fontWeight: 500, color: MUTED, ml: 0.75 }}>{share}</Box>
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------- Sütun grafiği (sıralı aralıklar)
// items: [{ label, value }] — verilen sırayla (ör. gün aralıkları) çizilir
const niceMax = (v) => {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
};

export function ColumnChart({ items, height = 260, valueFormatter = formatNumber }) {
  const grown = useGrowIn(dataKey(items));
  const rows = items || [];
  if (rows.length === 0) return <EmptyState height={height} />;

  const top = niceMax(Math.max(...rows.map((r) => Number(r.value) || 0)));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * top);
  const total = rows.reduce((s, r) => s + (Number(r.value) || 0), 0);
  const plotHeight = height - 28; // alt etiket alanı

  return (
    <Box sx={{ display: 'flex', height, fontFamily: 'Segoe UI' }}>
      {/* Y ekseni değerleri */}
      <Box sx={{ position: 'relative', width: 44, height: plotHeight, flexShrink: 0 }}>
        {ticks.map((t) => (
          <Typography
            key={t}
            sx={{
              position: 'absolute',
              right: 6,
              bottom: `${(t / top) * 100}%`,
              transform: 'translateY(50%)',
              fontSize: '10px',
              color: MUTED,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {formatNumber(t)}
          </Typography>
        ))}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Çizim alanı + yatay ızgara */}
        <Box sx={{ position: 'relative', height: plotHeight, borderBottom: `1px solid #cfd8dc` }}>
          {ticks.slice(1).map((t) => (
            <Box key={t} sx={{ position: 'absolute', left: 0, right: 0, bottom: `${(t / top) * 100}%`, borderTop: `1px solid ${GRID}` }} />
          ))}
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end' }}>
            {rows.map((r) => {
              const v = Number(r.value) || 0;
              const share = formatPercent(v, total);
              return (
                <Tooltip key={r.label} title={tooltipContent(r.label, v, share, valueFormatter)} placement="top" arrow followCursor>
                  <Box
                    sx={{
                      flex: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      cursor: 'default',
                      '&:hover': { backgroundColor: 'rgba(255,152,0,0.06)' },
                      '&:hover .col': { background: BAR_HOVER }
                    }}
                  >
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: INK, mb: 0.5 }}>{valueFormatter(v)}</Typography>
                    <Box
                      className="col"
                      sx={{
                        width: '100%',
                        maxWidth: 28,
                        height: grown ? `${(v / top) * 100}%` : '0%',
                        minHeight: v > 0 ? 3 : 0,
                        background: `linear-gradient(180deg, ${BAR} 0%, #F57C00 100%)`,
                        borderRadius: '4px 4px 0 0',
                        transition: 'height 0.8s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.15s'
                      }}
                    />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
        {/* X ekseni etiketleri */}
        <Box sx={{ display: 'flex', height: 28 }}>
          {rows.map((r) => (
            <Typography
              key={r.label}
              sx={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: '11px', color: INK_2, pt: 0.75, px: 0.25 }}
              noWrap
              title={r.label}
            >
              {r.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- Kart başlığındaki filtre
const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

export function MultiSelectFilter({ label, options, value, onChange, onApply, disabled }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Autocomplete
        multiple
        limitTags={1}
        disableCloseOnSelect
        size="small"
        options={options || []}
        value={value || []}
        getOptionLabel={(o) => o?.Dsc ?? ''}
        isOptionEqualToValue={(o, v) => o?.Id === v?.Id}
        onChange={(e, v) => onChange(v || [])}
        ListboxProps={{ sx: { fontSize: '13px' } }}
        noOptionsText="Seçenek yok"
        sx={{ width: 230 }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} sx={{ mr: 1, p: 0.5, '&.Mui-checked': { color: ORANGE } }} checked={selected} />
            {option?.Dsc}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value?.length ? '' : label}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff', fontSize: '12px', minHeight: 34, py: '0 !important' },
              '& .MuiChip-root': { height: 22, fontSize: '11px' }
            }}
          />
        )}
      />
      <Tooltip title="Filtreyi uygula">
        <span>
          <Button
            size="small"
            onClick={onApply}
            disabled={disabled}
            sx={{
              minWidth: 0,
              height: 34,
              px: 1.25,
              borderRadius: '10px',
              color: ORANGE,
              backgroundColor: DARK,
              '&:hover': { backgroundColor: '#212121' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
            }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

// ---------------------------------------------------------------- İç içe halkalar (oran göstergesi)
// rings: [{ label, value, total, color, track }] — dıştan içe çizilir, merkezde başlık + değer
export function RingChart({ rings, size = 180, stroke = 14, gap = 6, centerLabel, centerValue }) {
  const grown = useGrowIn(dataKey(rings));
  const c = size / 2;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        {rings.map((r, i) => {
          const radius = c - stroke / 2 - i * (stroke + gap);
          const circ = 2 * Math.PI * radius;
          const pct = r.total > 0 ? Math.min(1, Math.max(0, r.value / r.total)) : 0;
          return (
            <g key={r.label}>
              <circle cx={c} cy={c} r={radius} fill="none" stroke={r.track} strokeWidth={stroke} />
              <Tooltip title={`${r.label}: ${formatNumber(r.value)} (${formatPercent(r.value, r.total)})`} arrow placement="top" followCursor>
                <circle
                  cx={c}
                  cy={c}
                  r={radius}
                  fill="none"
                  stroke={r.color}
                  strokeWidth={stroke}
                  strokeLinecap="round"
                  strokeDasharray={circ}
                  strokeDashoffset={grown ? circ * (1 - pct) : circ}
                  style={{ transition: `stroke-dashoffset 1s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.15}s`, cursor: 'default' }}
                />
              </Tooltip>
            </g>
          );
        })}
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: INK, lineHeight: 1.1 }}>{centerValue}</Typography>
        <Typography sx={{ fontSize: '10px', color: INK_2, fontWeight: 600, textAlign: 'center', px: 3 }}>{centerLabel}</Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- Donut (parça-bütün)
// segments: [{ label, value, color }] — parçalar arası 2px boşluk, merkezde toplam
export function DonutChart({ segments, size = 180, stroke = 22, centerLabel, centerValue }) {
  const grown = useGrowIn(dataKey(segments));
  const c = size / 2;
  const radius = c - stroke / 2;
  const circ = 2 * Math.PI * radius;
  const total = segments.reduce((s, x) => s + (Number(x.value) || 0), 0);
  const gapLen = total > 0 && segments.filter((x) => x.value > 0).length > 1 ? 2 : 0;
  let offset = 0;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={c} cy={c} r={radius} fill="none" stroke="#eceff1" strokeWidth={stroke} />
        {total > 0 &&
          segments.map((seg) => {
            const v = Number(seg.value) || 0;
            const len = Math.max(0, (v / total) * circ - gapLen);
            const dash = grown ? len : 0;
            const el = (
              <Tooltip key={seg.label} title={`${seg.label}: ${formatNumber(v)} (${formatPercent(v, total)})`} arrow placement="top" followCursor>
                <circle
                  cx={c}
                  cy={c}
                  r={radius}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={stroke}
                  strokeDasharray={`${dash} ${circ}`}
                  strokeDashoffset={-offset}
                  style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.22, 1, 0.36, 1)', cursor: 'default' }}
                />
              </Tooltip>
            );
            offset += (v / total) * circ;
            return el;
          })}
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: INK, lineHeight: 1.1 }}>{centerValue}</Typography>
        <Typography sx={{ fontSize: '10px', color: INK_2, fontWeight: 600 }}>{centerLabel}</Typography>
      </Box>
    </Box>
  );
}

// Grafiğin yanındaki açıklama satırı (renk noktası + etiket + değer + pay)
export function LegendRow({ color, label, value, share }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderBottom: '1px solid #f0f0f0', '&:last-of-type': { borderBottom: 'none' } }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '12px', color: INK_2, fontWeight: 600, flex: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: '16px', color: INK, fontWeight: 700 }}>{value}</Typography>
      {share && <Typography sx={{ fontSize: '11px', color: MUTED, minWidth: 36, textAlign: 'right' }}>{share}</Typography>}
    </Box>
  );
}

// ---------------------------------------------------------------- Büyük rakam kartı (hero)
export function HeroStat({ icon, label, value, footer }) {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: '16px 18px',
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #fff8e1 0%, #ffffff 70%)',
        border: '1px solid #ffe0b2',
        height: '100%',
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -18,
          bottom: -22,
          color: 'rgba(239,108,0,0.08)',
          '& .MuiSvgIcon-root': { fontSize: 110 }
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '9px',
            backgroundColor: DARK,
            color: ORANGE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& .MuiSvgIcon-root': { fontSize: 18 }
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: INK_2 }}>{label}</Typography>
      </Box>
      <Typography sx={{ fontSize: '30px', fontWeight: 700, color: INK, lineHeight: 1.2, mt: 1.25, position: 'relative' }} noWrap>
        {value}
      </Typography>
      <Typography sx={{ position: 'relative', fontSize: '12px', color: INK_2, mt: 0.5, minHeight: '18px' }} noWrap>
        {footer}
      </Typography>
    </Box>
  );
}
